import React, { FC, useState } from "react";
import {
  useGetViolationsListQuery,
  useAddOrUpdateViolationsMutation,
} from "features/api/contentManagement/contentManagementApiSlice";
import * as Yup from "yup";
import BSButton from "react-bootstrap/Button";
import Stack from "react-bootstrap/Stack";
import Form from "react-bootstrap/Form";
import { PencilSquare } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import {
  ACCORDING_TO_CIRCUMSTANCES,
  DecisionTypesCode,
  DecisionsTypeArray,
} from "components/Reports/reportAssets/assets";
import RenderList from "./List/RenderList";
import {
  Violation,
  violationTabFields,
  ViolationsFormControlsValues,
  Decision,
} from "../contentManagementUtils/tabSchema";

import { Button } from "./Button";
import AddContentForm from "./AddContentForm";
import { ViolationsTabTypes } from "../contentManagementTypes";

const ViolationsTab: FC<ViolationsTabTypes> = () => {
  const { t } = useTranslation();
  const { data: violations } = useGetViolationsListQuery();
  const decisions = DecisionsTypeArray?.filter(
    (item) => item.code !== DecisionTypesCode.Obsolescence
  );
  const [addOrUpdateViolation] = useAddOrUpdateViolationsMutation();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState<Violation | null>(null);

  const toggleCreateForm = () => {
    setShowCreateForm(!showCreateForm);
  };

  const toggleUpdateForm = (initialValues: Violation | null) => {
    setShowUpdateForm(initialValues);
  };

  const violationsValidationSchema: Yup.ObjectSchema<ViolationsFormControlsValues> =
    Yup.object().shape({
      name: Yup.string().required(t("חובה להזין שם עבירה")),
      policy: Yup.string().required(t("חובה להזין מדיניות")),
      decisionTypeID: Yup.number()
        .nullable()
        .test(
          "required-based-on-policy",
          t("חובה לבחור החלטה"),
          (value, context) => {
            const { policy } = context.parent;
            return policy !== ACCORDING_TO_CIRCUMSTANCES
              ? value !== null
              : true;
          }
        ),
    });

  const violationsInitialValues: Violation = {
    id: 0,
    name: "",
    policy: "",
    decisionTypeID: null,
    disable: false,
    forVolunteer: false,
  };

  const handleCreateOrUpdateViolation: (values: Violation) => void = async (
    values: Violation
  ) => {
    const payload = {
      id: values.id,
      name: values.name,
      policy: values.policy,
      decisionTypeID: values.decisionTypeID,
      disable: values.disable,
    };

    await addOrUpdateViolation(payload).unwrap();

    // close modal
    values.id === 0 ? toggleCreateForm() : toggleUpdateForm(null);
  };

  const handleDisable = async (violation: Violation) => {
    const payload = {
      id: violation.id,
      name: violation.name,
      policy: violation.policy,
      decisionTypeID: violation.decisionTypeID,
      disable: violation.disable,
    };
    await addOrUpdateViolation(payload).unwrap();
  };

  const columns = [
    {
      title: t("קוד עבירה"),
      dataIndex: 1,
      key: "id",
      flex: 1,
      renderCell: (text: string) => <span>{text}</span>,
      asc: true,
      minWidth: 140,
    },
    {
      title: t("שם עבירה"),
      dataIndex: 2,
      key: "name",
      flex: 1,
      renderCell: (text: string) => <span>{text}</span>,
      asc: true,
      minWidth: 140,
    },
    {
      title: t("policy"),
      dataIndex: 3,
      key: "policy",
      flex: 1,
      renderCell: (text: string) => <span>{text}</span>,
      asc: true,
      minWidth: 140,
    },
    {
      title: t("decision"),
      dataIndex: 4,
      key: "decisionTypeID",
      flex: 1,
      renderCell: (decisionTypeID: number) => (
        <span>
          {decisions?.find((x: Decision) => x.code === decisionTypeID)?.name}
        </span>
      ),
      asc: true,
      minWidth: 140,
    },
    {
      title: t("חסום?"),
      dataIndex: 5,
      key: "actions",
      flex: 1,
      renderCell: (violation: Violation) => {
        return (
          <Stack direction="horizontal" gap={0}>
            <Form.Check
              type="switch"
              id="custom-switch"
              checked={violation.disable}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                handleDisable({ ...violation, disable: event.target.checked });
              }}
              size={20}
            />
          </Stack>
        );
      },
      asc: true,
      minWidth: 140,
    },
    {
      title: "",
      dataIndex: 6,
      key: "actions",
      flex: 1,
      renderCell: (violation: Violation) => {
        return (
          <Stack direction="horizontal" gap={0}>
            <div
              className={`button-wrapper ${
                violation.disable ? "disabled" : ""
              }`}
            >
              <BSButton
                key={violation.id}
                name={violation.name}
                variant="outline"
              >
                <PencilSquare
                  size={20}
                  color="primary"
                  onClick={() => {
                    toggleUpdateForm(violation);
                  }}
                />
              </BSButton>
            </div>
          </Stack>
        );
      },
      asc: true,
      minWidth: 100,
      maxWidth: 120,
    },
  ];

  return (
    <div className="tab-list-container">
      <div className="tab-list-header">
        <Button title={t("add")} handleClick={toggleCreateForm} />
      </div>
      <RenderList<Violation>
        data={
          violations
            ? [...violations].filter((item) => !item?.forVolunteer)
            : []
        }
        columns={columns}
        disableField="disable"
      />
      <AddContentForm
        isOpen={showCreateForm}
        toggle={toggleCreateForm}
        fields={violationTabFields}
        decisions={decisions}
        submitForm={handleCreateOrUpdateViolation}
        headerTitle={t("עבירה חדשה")}
        approveBtnLabel={t("יצירת עבירה")}
        cancelBtnLabel={t("cancel")}
        initialValues={violationsInitialValues}
        validationSchema={violationsValidationSchema}
      />
      <AddContentForm
        isOpen={!!showUpdateForm}
        toggle={toggleUpdateForm}
        fields={violationTabFields}
        decisions={decisions}
        submitForm={handleCreateOrUpdateViolation}
        headerTitle={t("עדכן עבירה")}
        approveBtnLabel={t("עדכן עבירה")}
        cancelBtnLabel={t("cancel")}
        initialValues={showUpdateForm}
        validationSchema={violationsValidationSchema}
      />
    </div>
  );
};

export default ViolationsTab;
